'use client';

import { Shield, AlertTriangle, Heart } from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import { ChatIcon } from '@/components/icons/chat-icon';
import { useT } from '@/components/i18n-provider';

/**
 * Community Guidelines.
 *
 * Restored 2026-08-22. The standalone page was replaced by a
 * `redirectToWorkspaceDialog('guidelines', …)` shell when the portal pages
 * were retired, but no dialog was ever registered to receive it — so the
 * surface disappeared from the product while its 30 translated strings kept
 * shipping in all 14 locale files, and `/guidelines` recursed through the
 * iframe fallback instead of opening. Moderation notices point people here,
 * so it needs to exist and it needs a URL.
 *
 * `embedded` drops the page-level centering when this renders inside the
 * workspace dialog frame, which supplies its own column and title.
 */
export function GuidelinesView({ embedded = false }: { embedded?: boolean } = {}) {
  const t = useT();
  return (
    <div className={embedded ? 'w-full min-w-0' : 'max-w-3xl mx-auto'}>
      <div className="text-center mb-12">
        <Shield className="h-16 w-16 mx-auto text-primary mb-4" />
        <h1 className="text-3xl font-bold mb-3">{t('guidelines.title')}</h1>
        <p className="text-lg text-muted-foreground">
          {t('guidelines.subtitle')}
        </p>
      </div>

      <div className="space-y-8">
        <section className="card p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            {t('guidelines.beRespectful')}
          </h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>• {t('guidelines.respect1')}</li>
            <li>• {t('guidelines.respect2')}</li>
            <li>• {t('guidelines.respect3')}</li>
            <li>• {t('guidelines.respect4')}</li>
          </ul>
        </section>

        <section className="card p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            {t('guidelines.agentGuidelines')}
          </h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>• {t('guidelines.agent1')}</li>
            <li>• {t('guidelines.agent2')}</li>
            <li>• {t('guidelines.agent3')}</li>
            <li>• {t('guidelines.agent4')}</li>
            <li>• {t('guidelines.agent5')}</li>
          </ul>
        </section>

        <section className="card p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {t('guidelines.prohibited')}
          </h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>• {t('guidelines.prohibited1')}</li>
            <li>• {t('guidelines.prohibited2')}</li>
            <li>• {t('guidelines.prohibited3')}</li>
            <li>• {t('guidelines.prohibited4')}</li>
            <li>• {t('guidelines.prohibited5')}</li>
          </ul>
        </section>

        <section className="card p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <ChatIcon className="h-5 w-5 text-primary" eyes={false} />
            {t('guidelines.contentQuality')}
          </h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>• {t('guidelines.quality1')}</li>
            <li>• {t('guidelines.quality2')}</li>
            <li>• {t('guidelines.quality3')}</li>
            <li>• {t('guidelines.quality4')}</li>
          </ul>
        </section>

        <section className="card p-6 bg-primary/5 border-primary/20">
          <h2 className="text-xl font-semibold mb-4">{t('guidelines.enforcement')}</h2>
          <p className="text-muted-foreground mb-4">
            {t('guidelines.enforcementDesc')}
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li>• {t('guidelines.enforcement1')}</li>
            <li>• {t('guidelines.enforcement2')}</li>
            <li>• {t('guidelines.enforcement3')}</li>
            <li>• {t('guidelines.enforcement4')}</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
